import React, { FunctionComponent, useCallback } from 'react';
import { MongoEvent } from 'interfaces/events.interface';
import { formatDateTime, PhoneNumber } from 'utils';
import { Table, Icon, Dimmer, Loader, Popup } from 'semantic-ui-react';
import { Link } from 'react-router-dom';
import { CallsTableGloballyBlockedQuery } from 'generated/graphql';
import ProFullName from 'components/pro-full-name/pro-full-name';
import CallDetails from 'components/call-details/call-details';
import { CallSummary } from 'components/call-summary/call-summary';
import { CallRecording } from 'components/call-recording/call-recording';
import { CallTranscription } from 'components/call-transcription/call-transcription';
import { useCurrentBilikZones } from 'context/current-bilik-zones-context';

type CallsTableViewProps = {
  calls: MongoEvent[];
  loading: boolean;
  filters: {
    currentBilikZoneId?: number;
    proViewId?: number;
  };
  orderBy;
  onSort?: (column: string, direction: string) => void;
  onFilter?: (column: string, value: string) => void;
  globallyBlocked: CallsTableGloballyBlockedQuery['globallyBlockedIdentity'];
};

const CallsTableView: FunctionComponent<CallsTableViewProps> = ({
  calls,
  filters,
  orderBy,
  loading,
  onSort,
  onFilter,
  globallyBlocked,
}) => {
  const { currentBilikZones } = useCurrentBilikZones();

  const getTelephone = useCallback((call: MongoEvent): string => {
    switch (call.version) {
      case 0:
        return call.data.callrCall.cliNumber;
      case 1:
        return call.data.callrCall.cli_number;
      default:
        return call.initiator.pi.telephone;
    }
  }, []);

  const getDuration = useCallback((call: MongoEvent): number => {
    switch (call.version) {
      case 0:
        return call.data.callrCall.durationAnswered;
      case 1:
        return call.data.callrCall.duration_answered;
      case 2:
      case 3:
        return call.data?.twilio?.CallDuration ?? call.data?.twilio?.Duration;
      case 4:
        return call.data.vonage.duration;
      default:
        return call.data.telnyx.CallDuration;
    }
  }, []);

  const getOperator = useCallback((call: MongoEvent): string => {
    if (call.data?.vonage) {
      return 'Vonage';
    }
    if (call.data?.telnyx) {
      return 'Telnyx';
    }
    if (call.data?.callrCall) {
      return 'Callr';
    }
    if (call.data?.twilio) {
      return 'Twilio';
    }
    return 'Inconnu';
  }, []);

  const onSortByDateClicked = useCallback((): void => {
    if (onSort) {
      onSort(
        'date',
        orderBy?.direction === 'ascending' ? 'descending' : 'ascending',
      );
    }
    if (onFilter) {
      onFilter('page', '1');
    }
  }, [onSort, onFilter, orderBy?.direction]);

  const isGloballyBlocked = useCallback((call: MongoEvent): boolean => {
    return globallyBlocked.some(
      (value) => value.identifier.split(':')[1] === getTelephone(call),
    );
  }, []);

  return (
    <Dimmer.Dimmable>
      <Dimmer inverted verticalAlign="top" active={loading}>
        <Loader style={{ marginTop: '20px' }} inline>
          Chargement...
        </Loader>
      </Dimmer>
      <Table striped celled sortable selectable>
        <Table.Header>
          <Table.Row>
            <Table.HeaderCell
              sorted={orderBy?.direction}
              onClick={onSortByDateClicked}
            >
              Date
            </Table.HeaderCell>
            <Table.HeaderCell className="not sortable">
              Numéro du particulier
            </Table.HeaderCell>
            <Table.HeaderCell className="not sortable">Durée</Table.HeaderCell>
            {!filters.proViewId ? (
              <>
                <Table.HeaderCell className="not sortable">
                  Pro / Zone
                </Table.HeaderCell>
              </>
            ) : null}
            <Table.HeaderCell className="not sortable">
              Enregistrement
            </Table.HeaderCell>
            <Table.HeaderCell className="not sortable" width={5}>
              Résumé
            </Table.HeaderCell>
          </Table.Row>
        </Table.Header>
        <Table.Body>
          {calls.map((call) => (
            <Table.Row key={call._id}>
              <Table.Cell>
                {formatDateTime(call.date)}{' '}
                {filters.proViewId ? (
                  <>
                    {call.data?.twilio?.Forwarded ||
                    call.data?.vonage?.forwarded ||
                    call.data?.telnyx?.Forwarded
                      ? '(Appel redirigé)'
                      : null}
                  </>
                ) : null}
              </Table.Cell>
              <Table.Cell>
                <Popup
                  position="top center"
                  trigger={
                    <Link
                      to={`/timeline/${encodeURIComponent(getTelephone(call))}`}
                      style={{
                        textDecoration: 'none',
                      }}
                    >
                      <Icon fitted name="time" />
                      <Icon fitted name="long arrow alternate down" />
                    </Link>
                  }
                  content="Timeline du particulier"
                />
                &nbsp; &nbsp;
                {PhoneNumber.format(getTelephone(call))}
                &nbsp; &nbsp;
                {isGloballyBlocked(call) ? (
                  <Popup
                    position="top center"
                    trigger={<Icon color="red" name="dont" />}
                    content="Bloqué"
                  />
                ) : null}
                <br />
                <small>via {getOperator(call)}</small>
              </Table.Cell>
              <Table.Cell>
                {Math.floor(getDuration(call) / 60)}m{getDuration(call) % 60}s
              </Table.Cell>
              {!filters.proViewId ? (
                <>
                  <Table.Cell>
                    {call.actors?.pro !== undefined ? (
                      <>
                        <Link
                          to={`/pros/update/${call.actors?.pro?.proView._id}/calls`}
                        >
                          <ProFullName
                            name={call.actors?.pro?.proPresentation.name}
                            mainCityCode={
                              !currentBilikZones && !filters.proViewId
                                ? call.actors?.pro?.bilikZone.mainCityCode
                                : undefined
                            }
                            mainTradeNames={
                              call.actors?.pro?.proPresentation.mainTrade?.name
                            }
                          />
                        </Link>
                      </>
                    ) : (
                      (call.actors?.bilik?.bilikZone.name ??
                      call.actors?.bilik?.bilikZone.mainCityCode)
                    )}
                  </Table.Cell>
                </>
              ) : null}
              <Table.Cell style={{ whiteSpace: 'nowrap' }}>
                <div style={{ display: 'flex', alignItems: 'center' }}>
                  <CallRecording
                    id={
                      call.data.vonage?.conversation_uuid ??
                      call.data.telnyx?.ParentCallSid
                    }
                  />
                  <CallTranscription
                    id={
                      call.data.vonage?.conversation_uuid ??
                      call.data.telnyx?.ParentCallSid
                    }
                    disabled={
                      getDuration(call) <= 2 ||
                      call.actors?.pro?.proView.isCallRecordable === false ||
                      call.actors?.pro?.proView.isTradeCallRecordable === false
                    }
                  />
                </div>
              </Table.Cell>
              <Table.Cell>
                <CallDetails
                  duration={getDuration(call)}
                  callRecordable={call.actors?.pro?.proView.isCallRecordable}
                  tradeCallRecordable={
                    call.actors?.pro?.proView.isTradeCallRecordable
                  }
                >
                  <CallSummary
                    id={
                      call.data.vonage?.conversation_uuid ??
                      call.data.telnyx?.ParentCallSid
                    }
                  />
                </CallDetails>
              </Table.Cell>
            </Table.Row>
          ))}
        </Table.Body>
      </Table>
    </Dimmer.Dimmable>
  );
};

export default CallsTableView;
